import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  distinctUntilChanged,
  map,
  shareReplay,
} from 'rxjs';
import {
  ActiveLoaderRecord,
  ActiveLoaderStatus,
  LoaderGroupState,
  LoaderOwner,
  LoaderRegistryState,
  ResolvedScopedLoader,
  ResolvedSectionLoader,
} from '../models/loading.models';

type LoaderFinalStatus = Extract<
  ActiveLoaderStatus,
  'completed' | 'cancelled' | 'timed-out'
>;

@Injectable({
  providedIn: 'root',
})
export class LoaderRegistryService {
  private readonly registryStateSubject = new BehaviorSubject<LoaderRegistryState>(
    this.createInitialState()
  );

  readonly state$: Observable<LoaderRegistryState> =
    this.registryStateSubject.asObservable();

  getSnapshot(): LoaderRegistryState {
    return this.registryStateSubject.value;
  }

  getRecordSnapshot(tokenId: string): ActiveLoaderRecord | undefined {
    return this.registryStateSubject.value.activeByTokenId[tokenId];
  }

  getReferenceTokenIds(referenceKey: string): string[] {
    return this.registryStateSubject.value.referencesByKey[referenceKey] ?? [];
  }

  addRecord(activeLoaderRecord: ActiveLoaderRecord): void {
    this.updateState((currentRegistryState) =>
      this.rebuildRegistryState({
        ...currentRegistryState,
        activeByTokenId: {
          ...currentRegistryState.activeByTokenId,
          [activeLoaderRecord.token.id]: activeLoaderRecord,
        },
      })
    );
  }

  markRecordVisible(tokenId: string, visibleAtEpochMs: number): void {
    this.updateRecord(tokenId, (activeLoaderRecord) => ({
      ...activeLoaderRecord,
      status: 'visible',
      visibleAtEpochMs,
    }));
  }

  updateRecordRefCount(tokenId: string, refCount: number): void {
    this.updateRecord(tokenId, (activeLoaderRecord) => ({
      ...activeLoaderRecord,
      refCount,
    }));
  }

  finalizeRecord(
    tokenId: string,
    finalStatus: LoaderFinalStatus,
    completedAtEpochMs: number
  ): void {
    this.updateRecord(tokenId, (activeLoaderRecord) => ({
      ...activeLoaderRecord,
      status: finalStatus,
      completedAtEpochMs,
    }));
  }

  removeRecord(tokenId: string): void {
    this.updateState((currentRegistryState) => {
      if (!currentRegistryState.activeByTokenId[tokenId]) {
        return currentRegistryState;
      }

      const nextActiveByTokenId = { ...currentRegistryState.activeByTokenId };
      delete nextActiveByTokenId[tokenId];

      return this.rebuildRegistryState({
        ...currentRegistryState,
        activeByTokenId: nextActiveByTokenId,
      });
    });
  }

  selectState(): Observable<LoaderRegistryState> {
    return this.state$;
  }

  selectAllActiveRecords(): Observable<ActiveLoaderRecord[]> {
    return this.state$.pipe(
      map((loaderRegistryState) =>
        this.sortRecords(
          Object.values(loaderRegistryState.activeByTokenId).filter(
            (activeLoaderRecord) => this.isTrackedStatus(activeLoaderRecord.status)
          )
        )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  selectActiveLoaderGroups(): Observable<LoaderGroupState[]> {
    return this.state$.pipe(
      map((loaderRegistryState) =>
        Object.values(loaderRegistryState.groupsByGroupId).sort(
          (firstLoaderGroup, secondLoaderGroup) =>
            firstLoaderGroup.firstStartedAtEpochMs -
            secondLoaderGroup.firstStartedAtEpochMs
        )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  selectActiveFullscreenLoader(): Observable<ActiveLoaderRecord | null> {
    return this.selectAllActiveRecords().pipe(
      map((activeLoaderRecords) => {
        const fullscreenLoaderRecords = activeLoaderRecords.filter(
          (activeLoaderRecord) =>
            activeLoaderRecord.status === 'visible' &&
            activeLoaderRecord.visibleMode === 'fullscreen-blocking'
        );

        return fullscreenLoaderRecords[0] ?? null;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  selectTopBarVisible(): Observable<boolean> {
    return this.selectAllActiveRecords().pipe(
      map((activeLoaderRecords) => {
        const hasVisibleFullscreenLoader = activeLoaderRecords.some(
          (activeLoaderRecord) =>
            activeLoaderRecord.status === 'visible' &&
            activeLoaderRecord.visibleMode === 'fullscreen-blocking'
        );

        if (hasVisibleFullscreenLoader) {
          return false;
        }

        return activeLoaderRecords.some(
          (activeLoaderRecord) =>
            activeLoaderRecord.status === 'visible' &&
            activeLoaderRecord.visibleMode === 'top-bar'
        );
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  selectSectionLoader(scopeKey: string): Observable<ResolvedSectionLoader | null> {
    return this.selectAllActiveRecords().pipe(
      map((activeLoaderRecords) => this.buildSectionLoaderMap(activeLoaderRecords)[scopeKey] ?? null),
      distinctUntilChanged((previousSectionLoader, nextSectionLoader) =>
        this.areSectionLoadersEqual(previousSectionLoader, nextSectionLoader)
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  selectVisibleSectionLoader(
    scopeKey: string
  ): Observable<ResolvedSectionLoader | null> {
    return this.selectAllActiveRecords().pipe(
      map((activeLoaderRecords) => {
        const sectionLoaderMap = this.buildSectionLoaderMap(activeLoaderRecords);

        if (this.hasVisibleFullscreenLoader(activeLoaderRecords)) {
          return null;
        }

        const resolvedSectionLoader = sectionLoaderMap[scopeKey];

        if (
          !resolvedSectionLoader ||
          resolvedSectionLoader.dominantMode !== 'section-blocking' ||
          resolvedSectionLoader.dominantRecord.status !== 'visible'
        ) {
          return null;
        }

        return resolvedSectionLoader;
      }),
      distinctUntilChanged((previousSectionLoader, nextSectionLoader) =>
        this.areSectionLoadersEqual(previousSectionLoader, nextSectionLoader)
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  selectAllVisibleSectionLoaders(): Observable<ResolvedSectionLoader[]> {
    return this.selectAllActiveRecords().pipe(
      map((activeLoaderRecords) => {
        if (this.hasVisibleFullscreenLoader(activeLoaderRecords)) {
          return [];
        }

        return Object.values(this.buildSectionLoaderMap(activeLoaderRecords))
          .filter(
            (resolvedSectionLoader) =>
              resolvedSectionLoader.dominantMode === 'section-blocking' &&
              resolvedSectionLoader.dominantRecord.status === 'visible'
          )
          .sort((firstSectionLoader, secondSectionLoader) => {
            if (
              secondSectionLoader.resolvedPriority !==
              firstSectionLoader.resolvedPriority
            ) {
              return (
                secondSectionLoader.resolvedPriority -
                firstSectionLoader.resolvedPriority
              );
            }

            return (
              firstSectionLoader.dominantRecord.startedAtEpochMs -
              secondSectionLoader.dominantRecord.startedAtEpochMs
            );
          });
      }),
      distinctUntilChanged((previousSectionLoaders, nextSectionLoaders) =>
        this.areSectionLoaderArraysEqual(previousSectionLoaders, nextSectionLoaders)
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  selectComponentLoader(scopeKey: string): Observable<ResolvedScopedLoader | null> {
    return this.selectScopedLoader('component', scopeKey);
  }

  selectItemLoader(scopeKey: string): Observable<ResolvedScopedLoader | null> {
    return this.selectScopedLoader('item', scopeKey);
  }

  selectInlineLoaders(scopeKey?: string): Observable<ResolvedScopedLoader[]> {
    return this.selectAllActiveRecords().pipe(
      map((activeLoaderRecords) =>
        this.buildScopedLoaderMap(activeLoaderRecords)
          .filter(
            (resolvedScopedLoader) =>
              resolvedScopedLoader.dominantMode === 'inline-nonblocking' &&
              (!scopeKey || resolvedScopedLoader.scopeKey === scopeKey)
          )
          .sort((firstScopedLoader, secondScopedLoader) => {
            if (
              secondScopedLoader.resolvedPriority !== firstScopedLoader.resolvedPriority
            ) {
              return (
                secondScopedLoader.resolvedPriority -
                firstScopedLoader.resolvedPriority
              );
            }

            return (
              firstScopedLoader.dominantRecord.startedAtEpochMs -
              secondScopedLoader.dominantRecord.startedAtEpochMs
            );
          })
      ),
      distinctUntilChanged((previousScopedLoaders, nextScopedLoaders) =>
        this.areScopedLoaderArraysEqual(previousScopedLoaders, nextScopedLoaders)
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private updateRecord(
    tokenId: string,
    updater: (activeLoaderRecord: ActiveLoaderRecord) => ActiveLoaderRecord
  ): void {
    this.updateState((currentRegistryState) => {
      const currentLoaderRecord = currentRegistryState.activeByTokenId[tokenId];

      if (!currentLoaderRecord) {
        return currentRegistryState;
      }

      return this.rebuildRegistryState({
        ...currentRegistryState,
        activeByTokenId: {
          ...currentRegistryState.activeByTokenId,
          [tokenId]: updater(currentLoaderRecord),
        },
      });
    });
  }

  private updateState(
    updater: (loaderRegistryState: LoaderRegistryState) => LoaderRegistryState
  ): void {
    const currentRegistryState = this.registryStateSubject.value;
    const nextRegistryState = updater(currentRegistryState);

    if (nextRegistryState !== currentRegistryState) {
      this.registryStateSubject.next(nextRegistryState);
    }
  }

  private rebuildRegistryState(
    loaderRegistryState: LoaderRegistryState
  ): LoaderRegistryState {
    const trackedLoaderRecords = Object.values(
      loaderRegistryState.activeByTokenId
    ).filter((activeLoaderRecord) => this.isTrackedStatus(activeLoaderRecord.status));

    return {
      ...loaderRegistryState,
      groupsByGroupId: this.buildLoaderGroupStateMap(trackedLoaderRecords),
      referencesByKey: this.buildReferenceStateMap(trackedLoaderRecords),
    };
  }

  private buildLoaderGroupStateMap(
    activeLoaderRecords: ActiveLoaderRecord[]
  ): Record<string, LoaderGroupState> {
    const recordsByGroupId = new Map<string, ActiveLoaderRecord[]>();

    activeLoaderRecords.forEach((activeLoaderRecord) => {
      const groupId = activeLoaderRecord.config.groupId;

      if (!groupId) {
        return;
      }

      const currentGroupRecords = recordsByGroupId.get(groupId) ?? [];
      recordsByGroupId.set(groupId, [...currentGroupRecords, activeLoaderRecord]);
    });

    return Array.from(recordsByGroupId.entries()).reduce<
      Record<string, LoaderGroupState>
    >((loaderGroupStateMap, [groupId, groupLoaderRecords]) => {
      const sortedGroupLoaderRecords = this.sortRecords(groupLoaderRecords);
      const dominantLoaderRecord = sortedGroupLoaderRecords[0];

      if (!dominantLoaderRecord) {
        return loaderGroupStateMap;
      }

      loaderGroupStateMap[groupId] = {
        groupId,
        scope: dominantLoaderRecord.config.scope,
        mode: dominantLoaderRecord.visibleMode,
        activeTokenIds: sortedGroupLoaderRecords.map(
          (activeLoaderRecord) => activeLoaderRecord.token.id
        ),
        refCount: sortedGroupLoaderRecords.reduce(
          (aggregateRefCount, activeLoaderRecord) =>
            aggregateRefCount + activeLoaderRecord.refCount,
          0
        ),
        firstStartedAtEpochMs: Math.min(
          ...sortedGroupLoaderRecords.map(
            (activeLoaderRecord) => activeLoaderRecord.startedAtEpochMs
          )
        ),
        lastUpdatedAtEpochMs: Math.max(
          ...sortedGroupLoaderRecords.map(
            (activeLoaderRecord) =>
              activeLoaderRecord.completedAtEpochMs ??
              activeLoaderRecord.visibleAtEpochMs ??
              activeLoaderRecord.startedAtEpochMs
          )
        ),
        ownerKeys: Array.from(
          new Set(
            sortedGroupLoaderRecords.map((activeLoaderRecord) =>
              this.buildOwnerKey(activeLoaderRecord.config.owner)
            )
          )
        ),
      };

      return loaderGroupStateMap;
    }, {});
  }

  private buildReferenceStateMap(
    activeLoaderRecords: ActiveLoaderRecord[]
  ): Record<string, string[]> {
    return [...activeLoaderRecords]
      .sort(
        (firstLoaderRecord, secondLoaderRecord) =>
          firstLoaderRecord.startedAtEpochMs - secondLoaderRecord.startedAtEpochMs
      )
      .reduce<Record<string, string[]>>((referenceStateMap, activeLoaderRecord) => {
        const referenceKey = activeLoaderRecord.config.referenceKey;

        if (!referenceKey) {
          return referenceStateMap;
        }

        referenceStateMap[referenceKey] = [
          ...(referenceStateMap[referenceKey] ?? []),
          activeLoaderRecord.token.id,
        ];

        return referenceStateMap;
      }, {});
  }

  private createInitialState(): LoaderRegistryState {
    return {
      activeByTokenId: {},
      groupsByGroupId: {},
      referencesByKey: {},
    };
  }

  private isTrackedStatus(activeLoaderStatus: ActiveLoaderStatus): boolean {
    return activeLoaderStatus === 'pending-delay' || activeLoaderStatus === 'visible';
  }

  private buildSectionLoaderMap(
    activeLoaderRecords: ActiveLoaderRecord[]
  ): Record<string, ResolvedSectionLoader> {
    return this.buildScopedLoaderMap(activeLoaderRecords, 'section').reduce<
      Record<string, ResolvedSectionLoader>
    >((sectionLoaderMap, resolvedScopedLoader) => {
      sectionLoaderMap[resolvedScopedLoader.scopeKey] = resolvedScopedLoader;
      return sectionLoaderMap;
    }, {});
  }

  private selectScopedLoader(
    scopeType: 'component' | 'item',
    scopeKey: string
  ): Observable<ResolvedScopedLoader | null> {
    return this.selectAllActiveRecords().pipe(
      map((activeLoaderRecords) => {
        const resolvedScopedLoader = this.buildScopedLoaderMap(
          activeLoaderRecords,
          scopeType
        ).find((currentScopedLoader) => currentScopedLoader.scopeKey === scopeKey);

        return resolvedScopedLoader ?? null;
      }),
      distinctUntilChanged((previousScopedLoader, nextScopedLoader) =>
        this.areScopedLoadersEqual(previousScopedLoader, nextScopedLoader)
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private buildScopedLoaderMap(
    activeLoaderRecords: ActiveLoaderRecord[],
    scopeType?: 'section' | 'component' | 'item'
  ): ResolvedScopedLoader[] {
    const scopedRecordsByScopeIdentity = new Map<string, ActiveLoaderRecord[]>();

    activeLoaderRecords.forEach((activeLoaderRecord) => {
      const activeScopeType = activeLoaderRecord.config.scope.type;

      if (
        (activeScopeType !== 'section' &&
          activeScopeType !== 'component' &&
          activeScopeType !== 'item') ||
        (scopeType && activeScopeType !== scopeType)
      ) {
        return;
      }

      const scopeIdentity = `${activeScopeType}:${activeLoaderRecord.config.scope.key}`;
      const existingScopedRecords = scopedRecordsByScopeIdentity.get(scopeIdentity) ?? [];

      scopedRecordsByScopeIdentity.set(scopeIdentity, [
        ...existingScopedRecords,
        activeLoaderRecord,
      ]);
    });

    return Array.from(scopedRecordsByScopeIdentity.entries())
      .map(([, scopedLoaderRecords]) => this.buildResolvedScopedLoader(scopedLoaderRecords))
      .filter(
        (
          resolvedScopedLoader
        ): resolvedScopedLoader is ResolvedScopedLoader => Boolean(resolvedScopedLoader)
      );
  }

  private buildResolvedScopedLoader(
    scopedLoaderRecords: ActiveLoaderRecord[]
  ): ResolvedScopedLoader | null {
    const sortedScopedLoaderRecords = this.sortRecords(scopedLoaderRecords);
    const dominantScopedLoaderRecord = sortedScopedLoaderRecords[0];

    if (!dominantScopedLoaderRecord) {
      return null;
    }

    return {
      scope: dominantScopedLoaderRecord.config.scope,
      scopeKey: dominantScopedLoaderRecord.config.scope.key,
      scopeType: dominantScopedLoaderRecord.config.scope.type,
      dominantMode: dominantScopedLoaderRecord.visibleMode,
      dominantRecord: dominantScopedLoaderRecord,
      activeTokenIds: sortedScopedLoaderRecords.map(
        (activeLoaderRecord) => activeLoaderRecord.token.id
      ),
      activeGroupIds: Array.from(
        new Set(
          sortedScopedLoaderRecords
            .map((activeLoaderRecord) => activeLoaderRecord.config.groupId)
            .filter((groupId): groupId is string => Boolean(groupId))
        )
      ),
      refCount: sortedScopedLoaderRecords.reduce(
        (aggregateRefCount, activeLoaderRecord) =>
          aggregateRefCount + activeLoaderRecord.refCount,
        0
      ),
      resolvedPriority: dominantScopedLoaderRecord.resolvedPriority,
      message: dominantScopedLoaderRecord.config.message,
      blocking: dominantScopedLoaderRecord.config.blocking ?? false,
    };
  }

  private hasVisibleFullscreenLoader(activeLoaderRecords: ActiveLoaderRecord[]): boolean {
    return activeLoaderRecords.some(
      (activeLoaderRecord) =>
        activeLoaderRecord.status === 'visible' &&
        activeLoaderRecord.visibleMode === 'fullscreen-blocking'
    );
  }

  private sortRecords(activeLoaderRecords: ActiveLoaderRecord[]): ActiveLoaderRecord[] {
    return [...activeLoaderRecords].sort((firstLoaderRecord, secondLoaderRecord) => {
      if (
        secondLoaderRecord.resolvedPriority !== firstLoaderRecord.resolvedPriority
      ) {
        return (
          secondLoaderRecord.resolvedPriority - firstLoaderRecord.resolvedPriority
        );
      }

      if (firstLoaderRecord.status !== secondLoaderRecord.status) {
        return firstLoaderRecord.status === 'visible' ? -1 : 1;
      }

      return firstLoaderRecord.startedAtEpochMs - secondLoaderRecord.startedAtEpochMs;
    });
  }

  private buildOwnerKey(loaderOwner: LoaderOwner): string {
    return `${loaderOwner.type}:${loaderOwner.name}`;
  }

  private areSectionLoadersEqual(
    previousSectionLoader: ResolvedSectionLoader | null,
    nextSectionLoader: ResolvedSectionLoader | null
  ): boolean {
    return this.areScopedLoadersEqual(previousSectionLoader, nextSectionLoader);
  }

  private areScopedLoadersEqual(
    previousScopedLoader: ResolvedScopedLoader | null,
    nextScopedLoader: ResolvedScopedLoader | null
  ): boolean {
    if (previousScopedLoader === nextScopedLoader) {
      return true;
    }

    if (!previousScopedLoader || !nextScopedLoader) {
      return false;
    }

    return (
      previousScopedLoader.scopeType === nextScopedLoader.scopeType &&
      previousScopedLoader.scopeKey === nextScopedLoader.scopeKey &&
      previousScopedLoader.dominantMode === nextScopedLoader.dominantMode &&
      previousScopedLoader.dominantRecord.token.id ===
        nextScopedLoader.dominantRecord.token.id &&
      previousScopedLoader.dominantRecord.status ===
        nextScopedLoader.dominantRecord.status &&
      previousScopedLoader.message === nextScopedLoader.message &&
      previousScopedLoader.refCount === nextScopedLoader.refCount &&
      previousScopedLoader.resolvedPriority === nextScopedLoader.resolvedPriority &&
      this.areStringArraysEqual(
        previousScopedLoader.activeTokenIds,
        nextScopedLoader.activeTokenIds
      ) &&
      this.areStringArraysEqual(
        previousScopedLoader.activeGroupIds,
        nextScopedLoader.activeGroupIds
      )
    );
  }

  private areSectionLoaderArraysEqual(
    previousSectionLoaders: ResolvedSectionLoader[],
    nextSectionLoaders: ResolvedSectionLoader[]
  ): boolean {
    return this.areScopedLoaderArraysEqual(previousSectionLoaders, nextSectionLoaders);
  }

  private areScopedLoaderArraysEqual(
    previousScopedLoaders: ResolvedScopedLoader[],
    nextScopedLoaders: ResolvedScopedLoader[]
  ): boolean {
    if (previousScopedLoaders.length !== nextScopedLoaders.length) {
      return false;
    }

    return previousScopedLoaders.every((previousScopedLoader, scopedLoaderIndex) =>
      this.areScopedLoadersEqual(
        previousScopedLoader,
        nextScopedLoaders[scopedLoaderIndex] ?? null
      )
    );
  }

  private areStringArraysEqual(
    previousValues: string[],
    nextValues: string[]
  ): boolean {
    if (previousValues.length !== nextValues.length) {
      return false;
    }

    return previousValues.every(
      (previousValue, previousIndex) => previousValue === nextValues[previousIndex]
    );
  }
}
